import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { ChangeEventType, FormErrors, FormValues } from "../AssignmentUploads";
import { usePortal } from "../../../../context/PortalsContext";
import { brand, gray } from "../../../../theme/themePrimitives";
import {
  AssignmentDependency,
  DdlAssignment,
} from "../../../../services/api/types/assignments/assignment";

interface ClientAdminFormProps {
  formValues: FormValues;
  errors: FormErrors;
  handleChange: (e: ChangeEventType) => void;
  handleSave: () => void;
  buttonLoading: boolean;
  isEditMode: boolean;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  portalOwner: string | null;
  dependencyOptions: DdlAssignment[];
}

export const ClientAdminForm: React.FC<ClientAdminFormProps> = ({
  formValues,
  errors,
  handleChange,
  handleSave,
  buttonLoading,
  isEditMode,
  setFormValues,
  portalOwner,
  dependencyOptions,
}) => {
  const { portals } = usePortal();
  const [showSubPortalsToggle, setShowSubportalsToggle] = useState(false);

  useEffect(() => {
    if (portals.portals.length > 0 && portals.selectedPortal && portalOwner) {
      const isOwnerMain = portals.portals.find(
        (p) => p.portalUid === portalOwner,
      )?.isMain;
      setShowSubportalsToggle(
        !portals.selectedPortal?.isMain &&
          !portals.selectedPortal?.isSubPortal &&
          !isOwnerMain,
      );
    }
  }, [portals.selectedPortal, portals.portals, portalOwner]);

  return (
    <Stack spacing={2}>
      <Stack spacing={2} width={"100%"}>
        <Stack spacing={1}>
          <Typography variant="subtitle2">
            Primary title
            <Box component="span" sx={{ color: `var(--error-700)` }}>
              *
            </Box>
          </Typography>
          <TextField
            fullWidth
            id="title"
            name="title"
            placeholder="Enter primary title"
            type="text"
            value={formValues.title}
            onChange={handleChange}
            variant="outlined"
            size="small"
            error={Boolean(errors.title)}
            helperText={errors.title}
            slotProps={{
              input: {
                endAdornment: errors.title && (
                  <ErrorOutlineIcon color="error" fontSize="small" />
                ),
              },
            }}
          />
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle2">Thumbnail title</Typography>
          <TextField
            fullWidth
            id="titleShort"
            name="titleShort"
            placeholder="Enter thumbnail title"
            type="text"
            value={formValues.titleShort}
            onChange={handleChange}
            variant="outlined"
            size="small"
            error={Boolean(errors.titleShort)}
            helperText={errors.titleShort}
            slotProps={{
              input: {
                endAdornment: errors.titleShort ? (
                  <ErrorOutlineIcon color="error" fontSize="small" />
                ) : (
                  <Tooltip
                    title="Displayed on assignment cards. Defaults to Primary Title if left empty."
                    arrow
                    placement="right"
                    slotProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: gray[800],
                        },
                      },
                      arrow: {
                        sx: {
                          color: gray[800],
                        },
                      },
                    }}
                  >
                    <HelpOutlineOutlinedIcon
                      sx={{
                        fontSize: 16,
                        color: "#98A2B3",
                        cursor: "pointer",
                      }}
                    />
                  </Tooltip>
                ),
              },
            }}
          />
        </Stack>
        <Stack spacing={1}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="subtitle2">Dependency</Typography>
            <Tooltip
              title="Defines assignment dependencies."
              arrow
              placement="right"
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: gray[800],
                  },
                },
                arrow: {
                  sx: {
                    color: gray[800],
                  },
                },
              }}
            >
              <HelpOutlineOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: "#98A2B3",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          </Box>
          <Autocomplete<DdlAssignment, true>
            multiple
            filterSelectedOptions
            options={dependencyOptions}
            value={dependencyOptions.filter((o) =>
              formValues.dependency.some(
                (d) => d.prerequisiteAssignmentUid === o.AssignmentUid,
              ),
            )}
            size="small"
            isOptionEqualToValue={(a, b) => a.AssignmentUid === b.AssignmentUid}
            getOptionLabel={(o) => o.title}
            onChange={(_, newValue) =>
              setFormValues((prev) => ({
                ...prev,
                dependency: newValue.map(
                  (o): AssignmentDependency => ({
                    prerequisiteAssignmentUid: o.AssignmentUid,
                    prerequisiteAssignmentId: o.AssignmentId,
                  }),
                ),
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select assignments"
                error={Boolean(errors.dependency)}
                helperText={errors.dependency}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: "44px",
                  },
                }}
              />
            )}
            renderValue={(value, getTagProps) => (
              <Box
                component="span"
                sx={{ display: "inline-flex", flexWrap: "wrap", gap: 1 }}
              >
                {value.map((option, index) => {
                  const { key, ...rest } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      {...rest}
                      label={option.title}
                      sx={{
                        backgroundColor: "white",
                        padding: "14px 6px",
                        borderRadius: "6px",
                      }}
                    />
                  );
                })}
              </Box>
            )}
            slotProps={{
              popupIndicator: { sx: { border: "none" } },
              clearIndicator: { sx: { display: "none" } },
            }}
          />
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle2">Max no. attempts</Typography>
          <TextField
            placeholder="Select no. attempts"
            type="number"
            value={formValues.maxAttempts}
            onChange={(e) => {
              const sanitized = e.target.value.replace(/[^0-9]/g, "");
              setFormValues((prev) => ({
                ...prev,
                maxAttempts: sanitized === "" ? undefined : Number(sanitized),
              }));
            }}
            onKeyDown={(e) =>
              ["-", "+", "e", "E", "."].includes(e.key) && e.preventDefault()
            }
            slotProps={{ htmlInput: { min: 0 } }}
            size="small"
            sx={{ "& .MuiInputBase-input": { px: "14px", py: "10px" } }}
          />
        </Stack>
        {showSubPortalsToggle && (
          <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.isAvailableToSubportals}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      isAvailableToSubportals: e.target.checked,
                    }))
                  }
                />
              }
              label="Available to subportals"
              sx={{
                marginLeft: 0,
                gap: "1rem",
                "& .MuiFormControlLabel-label": {
                  color: "black",
                },
              }}
            />
          </Box>
        )}
        {/* <Stack spacing={1}>
          <Typography variant="subtitle2" color={primaryTextColor}>
            Status
          </Typography>
          <Select
            disabled={isEditMode && formValues.status === undefined}
            name="status"
            IconComponent={ExpandMoreIcon}
            value={formValues.status || ""}
            error={Boolean(errors.status)}
            onChange={handleChange}
            sx={{
              borderRadius: "12px",
              minWidth: "150px",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
              },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                padding: "8.5px 14px",
              },
            }}
            size="medium"
          >
            <MenuItem value={Status.ACTIVE}>Active</MenuItem>
            <MenuItem value={Status.INACTIVE}>Inactive</MenuItem>
          </Select>
          {errors.status && (
            <Typography
              sx={{ margin: "4px 4px 0px !important" }}
              fontSize={"12px"}
              color={error[600]}
            >
              {errors.status}
            </Typography>
          )}
        </Stack> */}
      </Stack>
      <Box mt={2} sx={{ display: "flex", justifyContent: "center" }}>
        {!isEditMode && (
          <>
            {buttonLoading ? (
              <CircularProgress />
            ) : (
              <Button
                onClick={handleSave}
                sx={{
                  px: 2,
                  borderRadius: "12px",
                  backgroundColor: `${brand[600]} !important`,
                  color: `white`,
                }}
              >
                Continue
              </Button>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
};
